import { CssBaseline, StyledEngineProvider, Theme, ThemeProvider } from "@mui/material";
import React, { useEffect, useState } from "react";
import { BrowserRouter, Redirect, useLocation } from "react-router-dom";
import { useDataRequest } from "../../common/hooks/api";
import { theme as appTheme } from "../../common/root/appTheme";
import { Api } from "../../common/service/Api";
import { PageLoading } from "../components";
import {
  useOrganizationUsers,
  usePrimaryOrganization,
  usePrimaryOrganizationId,
  useSelf,
  useSession,
} from "../hooks";
import { GetCompliantPageRouter } from "./GetCompliantPageRouter";
import { StayCompliantPageRouter } from "./StayCompliantPageRouter";
import { MailboxDisconnectedBanner } from "../components/organisms/callouts/MailboxDisconnectedBanner";
import { InvalidIntegrationCredentialBanner } from "../components/organisms/callouts/InvalidIntegrationCredentialBanner";
import { configureBeacon, destroyBeacon, initBeacon, setIdentity } from "../util/beacon";
import { Routes } from "./routes";
import { getPhaseRedirect } from "./getPhaseRedirect";
import { useSelfOrganizationUser } from "../hooks/useOrganization";
import { GetCompliantPageWrapper } from "../components/layout/GetCompliantPageWrapper";
import {
  isHrCenterOnboardingComplete,
  useOnboardingProgress,
} from "../../common/hooks/useOnboardingProgress";
import {
  HR_CENTER_SETUP_BANNER_DISMISSED_COOKIE_NAME,
  HRCenterSetupBanner,
} from "../components/organisms/callouts/HRCenterSetupBanner";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { GlobalAdminEditBar } from "../components/layout/GlobalAdminEditBar";
import { RamsayChatSidebar } from "../components/layout/RamsayChatSidebar";
import { EditModeInteractionGate } from "../edit-mode/EditModeInteractionGate";
import { useEditMode } from "../edit-mode/EditModeContext";
import { setShouldEnforceEditMode } from "../edit-mode/editModeState";
import { usePostHogClient } from "../analytics/ProductAnalyticsProvider";
import { __PRODUCT_ANALYTICS_ENVIRONMENT__, __PRODUCT_ANALYTICS_RELEASE__ } from "../globals";

declare module "@mui/styles/defaultTheme" {
  interface DefaultTheme extends Theme {}
}

type WrappedRouterProps = {
  self: ReturnType<typeof useSelf>[0];
  selfRequest: ReturnType<typeof useSelf>[1];
};

const isRequestDetail = (pathname: string): boolean => {
  return (
    pathname.startsWith("/requests/") &&
    !["/requests/new", "/requests/active", "/requests/closed"].includes(pathname)
  );
};

const isPrivacyCenterPreviewPopup = (pathname: string): boolean => {
  return pathname === Routes.privacyCenter.previewPopup;
};

const isHrCenterPage = (pathname: string): boolean => {
  return pathname.startsWith(Routes.hrCenter.root);
};

const isDashboardPage = (pathname: string): boolean => {
  return pathname === Routes.dashboard.root;
};

export const LoggedInApp = () => {
  useSession();
  const [self, selfRequest] = useSelf();
  const { setIsEditMode } = useEditMode();
  const productAnalytics = usePostHogClient();
  const isGlobalAdmin = self?.globalRole === "ADMIN";

  useEffect(() => {
    if (!productAnalytics || !self?.id) {
      return;
    }

    productAnalytics.startSessionRecording();
    return () => productAnalytics.stopSessionRecording();
  }, [productAnalytics, self?.id]);

  useEffect(() => {
    if (!self) {
      setShouldEnforceEditMode(false);
      return () => setShouldEnforceEditMode(false);
    }

    setShouldEnforceEditMode(isGlobalAdmin);

    if (!isGlobalAdmin) {
      setIsEditMode(false);
    }

    return () => setShouldEnforceEditMode(false);
  }, [isGlobalAdmin, self, setIsEditMode]);

  return (
    <>
      <BrowserRouter>
        <WrappedRouter self={self} selfRequest={selfRequest} />
      </BrowserRouter>
      {isGlobalAdmin && (
        <>
          {/* Read-only/edit mode: block form edits before controls mutate local state. */}
          <EditModeInteractionGate />
        </>
      )}
    </>
  );
};

const WrappedRouter = ({ self, selfRequest }: WrappedRouterProps) => {
  const productAnalytics = usePostHogClient();
  const location = useLocation();
  const [locationPathname, setLocationPathname] = useState("");
  const [userIdentified, setUserIdentified] = useState(false);
  const organizationId = usePrimaryOrganizationId();
  const [organization, organizationRequest] = usePrimaryOrganization(true);
  const selfOrgUser = useSelfOrganizationUser();
  const [orgUsers, ,] = useOrganizationUsers(organizationId);
  const [onboardingProgress, onboardingProgressRequest] = useOnboardingProgress(organizationId);

  useEffect(() => {
    if (!productAnalytics || !organizationRequest.success || !organization || !self) {
      return;
    }

    const previousOrganizationId = productAnalytics.get_property("organization_id");
    if (previousOrganizationId && previousOrganizationId !== organization.id) {
      productAnalytics.reset();
    }

    if (productAnalytics.get_distinct_id() !== organization.id) {
      productAnalytics.identify(organization.id, {
        organization_id: organization.id,
        organization_name: organization.name,
        email: self.email,
      });
    }

    productAnalytics.register({
      organization_id: organization.id,
      organization_name: organization.name,
      email: self.email,
      app_environment: __PRODUCT_ANALYTICS_ENVIRONMENT__,
      app_release: __PRODUCT_ANALYTICS_RELEASE__,
      schema_version: 1,
    });
  }, [organization, organizationRequest.success, productAnalytics, self]);

  // Allow individual pages to show a custom banner (so we know not to show any default banners)
  const [showingCustomBanner, setShowingCustomBanner] = useState(false);

  const { request: featuresRequest } = useDataRequest({
    queryKey: ["features", organizationId],
    api: () => Api.organization.getOrganizationFeatures(organizationId),
  });

  const [dismissedHrCenterSetupBanner] = useLocalStorage(
    HR_CENTER_SETUP_BANNER_DISMISSED_COOKIE_NAME,
    "false",
  );

  useEffect(() => {
    if (selfRequest.success && self && !userIdentified) {
      setIdentity({
        name: `${self.firstName} ${self.lastName}`,
        email: self.email,
        signature: self.beaconSignature,
      });
      setUserIdentified(true);
    } else if (selfRequest.error) {
      destroyBeacon();
      initBeacon();
      configureBeacon();
    }
  }, [
    selfRequest,
    self,
    self?.beaconSignature,
    self?.email,
    self?.firstName,
    self?.lastName,
    userIdentified,
  ]);

  // HACK: When a location changes from a request detail page then close the beacon
  useEffect(() => {
    if (isRequestDetail(locationPathname) && locationPathname !== location.pathname) {
      configureBeacon();
      (window as any).Beacon("navigate", "/");
      (window as any).Beacon("close");
    }

    if (location.pathname !== locationPathname) {
      setLocationPathname(location.pathname);
    }
  }, [location, locationPathname]);

  useEffect(() => {
    if (organizationRequest.success && organization) {
      configureBeacon();
    }
  }, [organizationRequest, organization]);

  if (
    !self ||
    !selfOrgUser ||
    !organization ||
    organization.id !== organizationId ||
    organizationRequest.running ||
    // devSurveyAnswerRequest.running ||
    featuresRequest.running ||
    onboardingProgressRequest.running
  ) {
    // Can't route the user if we don't have all the necessary information that comes from these requests yet.
    return <PageLoading />;
  }

  const isAdmin = self.globalRole === "ADMIN";
  const isHrUser = selfOrgUser.role === "HR";
  const isMailboxDisconnected = !organization.mailbox.canSendEmail;

  const orgHasHrUser = orgUsers?.some((user) => user.role === "HR") ?? false;

  const isHrCenterSetup = isHrCenterOnboardingComplete(
    onboardingProgress?.hrCenterOnboardingProgress,
  );

  // Handle the case where an HR user tries to start the HR onboarding before the Business Survey has been submitted
  if (isHrUser && organization.phase === "GET_COMPLIANT") {
    return (
      <GetCompliantPageWrapper fullWidth={true} disablePadding={true}>
        <div className="get-compliant-complete--container mt-xl">
          <p>
            You cannot access the HR Onboarding Steps until the user who invited you completes the
            Business Survey Step.
          </p>
        </div>
      </GetCompliantPageWrapper>
    );
  }

  const phaseRedirect = getPhaseRedirect(location.pathname, organization);
  if (phaseRedirect) {
    // Redirect replaces history by default, so Back cannot revisit the invalid URL.
    return <Redirect to={phaseRedirect} />;
  }

  let showMailboxDisconnectedBanner =
    isMailboxDisconnected && !isPrivacyCenterPreviewPopup(locationPathname) && !isHrUser;
  let showInvalidIntegrationCredentialBanner =
    onboardingProgress?.workflowsSetupOnboardingProgress?.hasInvalidIntegrationCredential ===
      true &&
    !isPrivacyCenterPreviewPopup(locationPathname) &&
    !isDashboardPage(locationPathname) &&
    !isHrUser;
  let showHrCenterSetupBanner =
    !isHrCenterSetup &&
    !isHrUser &&
    isHrCenterPage(locationPathname) &&
    dismissedHrCenterSetupBanner !== "true";

  let router;
  switch (organization.phase) {
    case "GET_COMPLIANT":
      router = <GetCompliantPageRouter />;
      break;
    case "STAY_COMPLIANT":
    default:
      router = (
        <>
          {/* If a page has a custom banner, then show it, prioritized above any default banners */}
          {/* Priority: HR center setup > mailbox disconnected > invalid integration credential */}
          {!showingCustomBanner &&
            (showHrCenterSetupBanner ? (
              <HRCenterSetupBanner alreadyInvitedHrUser={orgHasHrUser} />
            ) : showMailboxDisconnectedBanner ? (
              <MailboxDisconnectedBanner />
            ) : (
              showInvalidIntegrationCredentialBanner && <InvalidIntegrationCredentialBanner />
            ))}
          <StayCompliantPageRouter
            isHrUser={isHrUser}
            setShowingCustomBanner={setShowingCustomBanner}
          />
        </>
      );
      break;
  }

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={appTheme}>
        <CssBaseline />
        {router}
        <RamsayChatSidebar />
        {isAdmin && (
          <GlobalAdminEditBar organizationId={organizationId} pathname={location.pathname} />
        )}
      </ThemeProvider>
    </StyledEngineProvider>
  );
};
